<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import BillingSectionCard from './BillingSectionCard.vue';
import { formatUsdFromCents } from '../helpers/money';
import { formatBillingDateTime } from '../helpers/formatBillingTime';
import { fetchBillingCashPayment } from '../api';

const props = defineProps({
  accountId: { type: [String, Number], required: true },
  invoices: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  error: { type: String, default: '' },
});

const { t } = useI18n();

const selectedId = ref(null);
const detail = ref(null);
const detailLoading = ref(false);
const detailError = ref('');

const statusLabel = status => {
  const key = `CUSTOM.BILLING.INVOICES.STATUS_${String(status || '').toUpperCase()}`;
  const translated = t(key);
  return translated !== key ? translated : status;
};

const openDetail = async id => {
  selectedId.value = id;
  detailLoading.value = true;
  detailError.value = '';
  detail.value = null;
  try {
    detail.value = await fetchBillingCashPayment(props.accountId, id);
  } catch (err) {
    detailError.value = err?.response?.data?.message || t('CUSTOM.BILLING.ERRORS.INVOICES');
  } finally {
    detailLoading.value = false;
  }
};

const closeDetail = () => {
  selectedId.value = null;
  detail.value = null;
  detailError.value = '';
};

watch(
  () => props.invoices,
  () => {
    if (selectedId.value && !props.invoices.some(i => i.id === selectedId.value)) {
      closeDetail();
    }
  }
);

const hasRows = computed(() => props.invoices.length > 0);
</script>

<template>
  <BillingSectionCard
    :title="t('CUSTOM.BILLING.INVOICES.TITLE')"
    :description="t('CUSTOM.BILLING.INVOICES.DESCRIPTION')"
  >
    <div v-if="loading" class="text-sm text-n-slate-11">
      {{ t('CUSTOM.COMMON.LOADING') }}
    </div>
    <div v-else-if="error" class="text-sm text-n-ruby-9" role="alert">
      {{ t('CUSTOM.BILLING.ERRORS.INVOICES') }}: {{ error }}
    </div>
    <div v-else-if="!hasRows" class="text-sm text-n-slate-11">
      {{ t('CUSTOM.BILLING.INVOICES.EMPTY') }}
    </div>
    <div v-else class="overflow-x-auto rounded-lg border border-n-weak">
      <table class="w-full text-sm text-start">
        <thead class="bg-n-alpha-1 text-n-slate-11">
          <tr>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.INVOICES.COL_NUMBER') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.INVOICES.COL_TIME') }}
            </th>
            <th class="px-3 py-2 font-medium text-end">
              {{ t('CUSTOM.BILLING.INVOICES.COL_AMOUNT') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.INVOICES.COL_STATUS') }}
            </th>
            <th class="px-3 py-2 font-medium">
              {{ t('CUSTOM.BILLING.INVOICES.COL_PURPOSE') }}
            </th>
            <th class="px-3 py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in invoices"
            :key="row.id"
            class="border-t border-n-weak text-n-slate-12"
          >
            <td class="px-3 py-2 font-medium tabular-nums">
              {{ row.invoice_number }}
            </td>
            <td class="px-3 py-2 whitespace-nowrap">
              {{ formatBillingDateTime(row.paid_at || row.created_at) }}
            </td>
            <td class="px-3 py-2 text-end tabular-nums">
              {{ formatUsdFromCents(row.amount_cents) }}
            </td>
            <td class="px-3 py-2">{{ statusLabel(row.status) }}</td>
            <td class="px-3 py-2 text-n-slate-11">
              {{ row.purpose_label || row.purpose }}
            </td>
            <td class="px-3 py-2 text-end">
              <Button
                size="sm"
                color="slate"
                variant="ghost"
                :label="t('CUSTOM.BILLING.INVOICES.VIEW')"
                @click="openDetail(row.id)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="selectedId"
      class="mt-4 rounded-lg border border-n-weak bg-n-solid-1 p-4"
    >
      <div class="flex items-start justify-between gap-3">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('CUSTOM.BILLING.INVOICES.DETAIL_TITLE') }}
        </div>
        <Button
          size="sm"
          color="slate"
          variant="ghost"
          :label="t('CUSTOM.BILLING.INVOICES.CLOSE')"
          @click="closeDetail"
        />
      </div>

      <div v-if="detailLoading" class="mt-3 text-sm text-n-slate-11">
        {{ t('CUSTOM.COMMON.LOADING') }}
      </div>
      <div v-else-if="detailError" class="mt-3 text-sm text-n-ruby-9" role="alert">
        {{ detailError }}
      </div>
      <div v-else-if="detail" class="mt-3 space-y-2 text-sm text-n-slate-12">
        <div>
          <span class="text-n-slate-11">{{ t('CUSTOM.BILLING.INVOICES.COL_NUMBER') }}:</span>
          {{ detail.invoice_number }}
        </div>
        <div>
          <span class="text-n-slate-11">{{ t('CUSTOM.BILLING.INVOICES.COL_PURPOSE') }}:</span>
          {{ detail.purpose_label || detail.purpose }}
        </div>
        <div>
          <span class="text-n-slate-11">{{ t('CUSTOM.BILLING.INVOICES.COL_AMOUNT') }}:</span>
          {{ formatUsdFromCents(detail.amount_cents) }}
        </div>
        <div>
          <span class="text-n-slate-11">{{ t('CUSTOM.BILLING.INVOICES.COL_STATUS') }}:</span>
          {{ statusLabel(detail.status) }}
        </div>
        <div v-if="detail.refunded_cents">
          <span class="text-n-slate-11">{{ t('CUSTOM.BILLING.INVOICES.REFUNDED') }}:</span>
          {{ formatUsdFromCents(detail.refunded_cents) }}
        </div>
        <div v-if="detail.receipt_url">
          <a
            :href="detail.receipt_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-11 underline"
          >
            {{ t('CUSTOM.BILLING.INVOICES.RECEIPT') }}
          </a>
        </div>
      </div>
    </div>
  </BillingSectionCard>
</template>
